import type { AdminUserDetail, CorrectCompanyClaimBody } from '@nestjs-boilerplate/types';
import { IndustryEnum } from '@nestjs-boilerplate/types';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCorrectCompanyClaim } from '../api/users';

/** The claim as the detail endpoint returns it. */
type ClaimedCompany = NonNullable<AdminUserDetail['claimedCompany']>;

/** Text fields, in the order they appear in the form. */
const FIELDS = [
  { key: 'companyName', label: 'Name' },
  { key: 'companyNumber', label: 'Companies House number' },
  { key: 'companyWebsite', label: 'Website' },
  { key: 'addressLine1', label: 'Address line 1' },
  { key: 'addressLine2', label: 'Address line 2' },
  { key: 'townCity', label: 'Town or city' },
  { key: 'county', label: 'County' },
  { key: 'postcode', label: 'Postcode' },
  { key: 'country', label: 'Country' },
] as const;

type FieldKey = (typeof FIELDS)[number]['key'];

/** The claim's own field names differ from the form's; map one to the other. */
function initialValues(claim: ClaimedCompany): Record<FieldKey, string> {
  return {
    companyName: claim.name,
    companyNumber: claim.companyNumber ?? '',
    companyWebsite: claim.website ?? '',
    addressLine1: claim.address.addressLine1 ?? '',
    addressLine2: claim.address.addressLine2 ?? '',
    townCity: claim.address.townCity ?? '',
    county: claim.address.county ?? '',
    postcode: claim.address.postcode ?? '',
    country: claim.address.country,
  };
}

/**
 * Corrects the company details a pending applicant claimed.
 *
 * The other half of keeping the approval modal a confirmation: a wrong claim is
 * fixed here, before approving, rather than by rejecting the applicant.
 *
 * Deliberately does NOT preview which company the corrected number will match.
 * The administrator sees that at the moment they approve, where the modal
 * re-resolves it — stating the same consequence here as well would put a copy
 * on screen that can go stale between saving and approving.
 */
export function CorrectClaimDialog({ userId, claim }: { userId: string; claim: ClaimedCompany }) {
  const [open, setOpen] = useState(false);
  const [values, setValues] = useState(() => initialValues(claim));
  const [industry, setIndustry] = useState(claim.industry);
  const correct = useCorrectCompanyClaim();

  function openDialog() {
    // Reset from the claim each time, so a cancelled edit does not persist
    // into the next one.
    setValues(initialValues(claim));
    setIndustry(claim.industry);
    setOpen(true);
  }

  function handleSave() {
    // Only what the administrator actually changed.
    //
    // Sending every field would make most claims uncorrectable. The claim's
    // address columns are nullable, but the validation is picked from the setup
    // schema where they are required — so a null field, rendered here as '',
    // would be echoed back as '' and rejected. An administrator fixing only a
    // company number on a claim with no town would be told the town is
    // required, and rejection would once again be the only remedy, which is
    // what this dialog exists to avoid.
    const initial = initialValues(claim);
    const patch: CorrectCompanyClaimBody = {};

    for (const { key } of FIELDS) {
      if (values[key] !== initial[key]) {
        patch[key] = values[key];
      }
    }

    if (industry !== claim.industry) {
      patch.industry = industry as never;
    }

    if (Object.keys(patch).length === 0) {
      setOpen(false);
      return;
    }

    // Closes only on success — a validation failure from the server must not
    // look like it saved.
    correct.mutate({ id: userId, ...patch }, { onSuccess: () => setOpen(false) });
  }

  return (
    <>
      <Button type="button" variant="outline" size="sm" onClick={openDialog}>
        Edit
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Correct the claimed company</DialogTitle>
            <DialogDescription>
              Fix what the applicant entered before approving them. Recorded separately from the
              approval.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 sm:grid-cols-2">
            {FIELDS.map(({ key, label }) => (
              <div key={key} className="space-y-2">
                <Label htmlFor={`claim-${key}`}>{label}</Label>
                <Input
                  id={`claim-${key}`}
                  value={values[key]}
                  onChange={(event) =>
                    setValues((prev) => ({ ...prev, [key]: event.target.value }))
                  }
                />
              </div>
            ))}

            <div className="space-y-2">
              <Label htmlFor="claim-industry">Industry</Label>
              <Select value={industry} onValueChange={setIndustry}>
                <SelectTrigger id="claim-industry">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {IndustryEnum.options.map((option) => (
                    <SelectItem key={option} value={option}>
                      {option.replaceAll('_', ' ').toLowerCase()}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" disabled={correct.isPending} onClick={handleSave}>
              {correct.isPending ? 'Saving…' : 'Save correction'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
